// file: apps/web-pharmacy/src/components/vente/SecoursActions.jsx
/**
 * PHARMAKON — les DEUX boutons du résultat de recherche (batch-1 vague 2).
 *
 * PLAN_pharmacy_batch1_modifications §2.3 · commanditaire §13 :
 * « même recherche produit, deux boutons : *Signaler une rupture* /
 *   *Vendre comme secours* ».
 *
 * Un seul composant pour les deux surfaces de vente (Libre et CHIFA), parce que
 * le geste est le MÊME au comptoir et qu'un jour où ils divergeraient, ce serait
 * un bug d'interface, pas une intention.
 *
 * CONFORT 55+ ET CLAVIER (règle maison) :
 *   · cibles ≥ 44 px de haut, texte ≥ 13 px — aucune icône seule ;
 *   · les deux boutons sont dans l'ordre de tabulation, `Entrée`/`Espace` les
 *     activent (ce sont de vrais `<button>`, pas des `<div onClick>`) ;
 *   · `stopPropagation` : la ligne de résultat est elle-même cliquable, un clic
 *     sur un bouton ne doit pas AUSSI déclencher la ligne.
 */
import { Ico } from "../ui/index.jsx";
import { SECOURS_COPIE } from "../../config/secours.config.js";

const CSS_ID = "pharmakon-secours-actions-css";
const CSS = `
.sec-actions{display:flex;gap:8px;margin-top:8px;flex-wrap:wrap;}
/* 44 px : le plancher de confort du POS (§2.3 — « cibles ≥ 44 px »). Ces deux
   boutons vivent DANS une liste de résultats qu'on parcourt vite ; sous 44 px,
   un doigt de 55 ans en manque un sur trois. */
.sec-btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;
  min-height:44px;padding:10px 14px;border-radius:var(--radius-sm);cursor:pointer;
  font-family:var(--font-ui);font-size:13px;font-weight:700;line-height:1.1;
  border:1px solid transparent;transition:filter 120ms,box-shadow 120ms;}
.sec-btn:focus-visible{outline:none;box-shadow:0 0 0 3px var(--accent-soft);}
.sec-btn:hover{filter:brightness(1.06);}
.sec-btn-rupture{background:var(--warning-soft);color:var(--warning);border-color:var(--warning);}
.sec-btn-secours{background:var(--accent);color:#fff;}
.sec-btn[disabled]{opacity:.55;cursor:not-allowed;filter:none;}
.sec-hint{margin-top:6px;font-size:12px;color:var(--text-2);}
.sec-hint-bloc{color:var(--danger);font-weight:600;}
@media (max-width:520px){ .sec-actions{flex-direction:column;} .sec-btn{width:100%;} }
`;

function ensureStyles() {
  if (typeof document === "undefined" || document.getElementById(CSS_ID)) return;
  const el = document.createElement("style");
  el.id = CSS_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

/**
 * @param {object}   props
 * @param {object}   props.produit        le produit posSearch concerné
 * @param {Function} props.onRupture      (produit) => void
 * @param {Function} props.onSecours      (produit) => void
 * @param {boolean} [props.plafondAtteint] plafond d'officine atteint (Q1) —
 *                   le bouton secours est alors désactivé AVEC son motif écrit,
 *                   jamais grisé en silence.
 * @param {string}  [props.motifBlocage]   raison de désactivation, en français
 */
export default function SecoursActions({
  produit,
  onRupture,
  onSecours,
  plafondAtteint = false,
  motifBlocage = null,
}) {
  ensureStyles();
  const stop = (fn) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    fn?.(produit);
  };
  const bloque = plafondAtteint || !!motifBlocage;

  return (
    <div className="sec-actions" onClick={(e) => e.stopPropagation()}>
      {typeof onRupture === "function" && (
        <button
          type="button"
          className="sec-btn sec-btn-rupture"
          onClick={stop(onRupture)}
          title="Enregistre une rupture au journal des manquants du responsable"
        >
          <Ico n="bell-plus" s={14} />
          {SECOURS_COPIE.rupture}
        </button>
      )}
      {typeof onSecours === "function" && (
        <button
          type="button"
          className="sec-btn sec-btn-secours"
          onClick={stop(onSecours)}
          disabled={bloque}
          title={
            bloque
              ? (motifBlocage ?? "Plafond de ventes de secours atteint")
              : "La boîte est sur le comptoir mais son lot n'est pas encore saisi"
          }
        >
          <Ico n="package" s={14} />
          {SECOURS_COPIE.vendre}
        </button>
      )}
      {bloque && (
        <div className="sec-hint sec-hint-bloc">
          {motifBlocage ??
            "Plafond de ventes de secours atteint — saisissez les achats correspondants."}
        </div>
      )}
    </div>
  );
}
